/**
 * ユーザー招待モーダル (権限管理 SBI-8)。
 *
 * メールアドレスを入力して `POST /users` を呼ぶ。Cognito が一時パスワード付きの招待メールを送る。
 * 招待直後のユーザーは所属ゼロ = 権限なしなので、権限付与画面でグループを付与するよう案内する。
 *
 * - すでに登録済み (409) / 形式不正 (400) はメール欄の直下に日本語で表示し、モーダルは開いたままにする。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import type { UsersClient } from '../api/users';
import type { AdminUser } from '../api/types';
import { useConfirmDialog } from '../hooks/useConfirmDialog';

import { AdminFormModal, FieldError } from './AdminFormModal';
import { PERMISSION_ADMIN_REQUIRED_MESSAGES, describeAdminError } from './permissionAdminMessages';

export interface UserInviteModalProps {
  apiClient: UsersClient;
  /** 招待成功時に呼ばれる */
  onInvited: (user: AdminUser) => void;
  onClose: () => void;
}

export function UserInviteModal(props: UserInviteModalProps): React.JSX.Element {
  const { apiClient, onInvited, onClose } = props;
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { confirmDiscard, confirmDialog } = useConfirmDialog();
  const emailRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (emailError) emailRef.current?.focus();
  }, [emailError]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const trimmed = email.trim();
    if (trimmed === '') {
      setEmailError(PERMISSION_ADMIN_REQUIRED_MESSAGES.email);
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    setEmailError(undefined);
    try {
      onInvited(await apiClient.invite(trimmed));
    } catch (err) {
      const info = describeAdminError(err);
      if (info.field === 'email') setEmailError(info.message);
      else setErrorMessage(info.message);
      setSaving(false);
    }
  }, [apiClient, email, onInvited]);

  /** 閉じる: 入力があれば破棄の確認を挟む。 */
  const handleClose = useCallback((): void => {
    if (email.trim() !== '') {
      confirmDiscard(onClose);
      return;
    }
    onClose();
  }, [confirmDiscard, email, onClose]);

  return (
    <AdminFormModal
      testIdPrefix="user-invite"
      title="ユーザーを招待"
      saveLabel="招待する"
      saving={saving}
      errorMessage={errorMessage}
      onDismissError={() => setErrorMessage(null)}
      onSubmit={() => void handleSubmit()}
      onClose={handleClose}
      maxWidth={520}
      overlay={confirmDialog}
    >
      <p className="form-required-hint">
        <span className="required-mark">必須</span> がついた項目は必ず入力してください
      </p>
      <label>
        <span className="required">メールアドレス</span>
        <input
          type="email"
          data-testid="user-invite-email"
          value={email}
          readOnly={saving}
          autoComplete="off"
          placeholder="user@example.com"
          ref={emailRef}
          className={emailError ? 'is-invalid' : ''}
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? 'user-invite-email-error' : undefined}
          onChange={(e) => {
            setEmail(e.target.value);
            setEmailError(undefined);
          }}
        />
        <FieldError id="user-invite-email-error" message={emailError} />
        <small>
          一時パスワード付きの招待メールを送ります。招待直後のユーザーは所属グループがないため、何も利用できません。
          「権限付与」画面でグループを付与してください。
        </small>
      </label>
    </AdminFormModal>
  );
}
